<template>
  <layout>
    <div class="container my-4">
      <loading-overlay v-if="submitting" />
      <div class="row">
        <div class="col-md-12">
          <b-card>
            <div
              v-if="!ready"
              class="d-flex flex-column align-items-center mt-4 mb-4"
            >
              <b-spinner
                style="width: 4rem; height: 4rem"
                variant="primary"
                label="Spinning"
              />
            </div>

            <div class="document-toolbar" v-if="ready">
              <div>
                <b-button
                  @click="goBack()"
                  variant="link"
                  size="sm"
                  class="font-weight-bold"
                >
                  <i class="fa fa-arrow-left" />
                  Back
                </b-button>
              </div>

              <div>
                {{ formComputed.name }}
                {{
                  sharedDocument.sentBy
                    ? `Sent by ${sharedDocument.sentBy?.fullName} On`
                    : ``
                }}
                {{ sharedDocument.sentAt | formatDate }}
              </div>
              <div v-if="sharedDocument.readOnly">
                <i class="fa fa-print" />
                <b-link @click="printCustom"> Print</b-link>
              </div>
            </div>

            <b-row v-if="ready && isActionRequired">
              <b-col>
                <div class="text-center document-container" v-if="form">
                  <div class="builder w-100">
                    <div class="text-left" v-if="preFormComponent">
                      <component
                        :is="preFormComponent"
                        :documentId="sharedDocument.document?.id"
                        :eocId="sharedDocument.episodeOfCareId ?? null"
                        ref="preFormComponent"
                      ></component>
                    </div>
                    <fb-form-viewer
                      :json="json.layout"
                      showType="e-form"
                      ref="formViewer"
                      @updateTotalPages="n => (totalPages = n)"
                    />
                  </div>
                </div>

                <div class="d-flex justify-content-between">
                  <b-button
                    :disabled="submitting"
                    size="sm"
                    variant="primary"
                    @click="submitForm()"
                    v-if="isActionRequired"
                  >
                    Complete and Submit
                  </b-button>
                  <div
                    v-if="validationErrorMessage"
                    class="text-danger text-left"
                  >
                    {{ validationErrorMessage }}
                  </div>
                </div>
              </b-col>
            </b-row>
            <b-row
              v-if="!isActionRequired"
              v-html-sanitize="documentRendered"
              class="document-render-container mt-4"
            >
              <!-- This Document is complete and submitted. -->
            </b-row>
          </b-card>
        </div>
      </div>
    </div>
  </layout>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import Layout from "../workspace/Layout.vue";
import eocServer from "../../server/eoc";
import scheduleEventServer from "../../server/scheduleEvents";
import catalogServer from "../../server/catalog";
import formServer from "../../server/form";
import formVersionServer from "../../server/formVersion";
import documentServer from "../../server/document";
import { getFormBuilderComponents } from "../../../../Breeze.FormBuilder.Frontend/helpers/formBuilder";
import { formatDateOnlyMysql } from "../../../../Breeze.FormBuilder.Frontend/helpers/date";
import scheduledMedicationServer from "../../server/scheduledMedication";
import DocumentReleaseOfInformation from "../documents/DocumentReleaseOfInformation.vue";
import LoadingOverlay from "../layout/LoadingOverlay.vue";

export default {
  name: "Document",
  components: {
    Layout,
    LoadingOverlay
  },
  data() {
    return {
      sharedDocumentId: this.$route.params.sharedDocumentId,
      documentRendered: "",
      singleBlobUrl: null,
      singleUrl: null,
      singleHtml: null,
      sharedDocument: {
        readOnly: true,
        document: {
          status: "loading"
        }
      },
      form: {
        name: "",
        current: {
          createdAt: "",
          createdBy: {
            firstName: "",
            lastName: "",
            fullName: ""
          }
        }
      },
      submit: null,
      validationErrorMessage: "",
      eoc: null,
      documentStatus: "Pending",
      ready: false,
      localSubmitId: null,
      submitting: false,
      totalPages: 1,
      saveROI: false
    };
  },
  async mounted() {
    await this.initializeFormSubmitter();
    if (!this.isActionRequired) {
      this.renderDocument();
    }
  },
  methods: {
    ...mapMutations("formSubmitter", [
      "setInitialValues",
      "setFormSubmitterFormHasUnsavedChanges",
      "setDocumentData"
    ]),
    ...mapActions("formSubmitter", [
      "initializeFormSubmitterForm",
      "addFormSubmitterForm",
      "editFormSubmitterForm",
      "validateFormSubmitterForm",
      "copyFormValuesToClipboard",
      "pasteFormValuesFromClipboard"
    ]),
    ...mapActions("session", ["loadDocuments"]),
    renderDocument() {
      if (this.sharedDocument.document?.id) {
        documentServer
          .render(this.sharedDocument.document.id)
          .then(res => {
            this.documentRendered = res.data;
          })
          .catch(console.error);
      }
    },
    async printCustom() {
      this.singleUrl = documentServer.documentViewUrl([
        this.sharedDocument.document.id
      ]);
      var response = await fetch(this.singleUrl.url, {
        headers: this.singleUrl.headers
      });
      var blob = await response.blob();
      this.singleBlobUrl = URL.createObjectURL(blob);
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.zIndex = -100;
      iframe.style.width = "2250px";
      iframe.src = this.singleBlobUrl;
      iframe.onload = function() {
        iframe.contentWindow.onafterprint = function() {
          document.body.removeChild(iframe);
        };

        setTimeout(() => {
          iframe.contentWindow.print();
        }, 500);
      };
      document.body.appendChild(iframe);
    },
    async initializeFormSubmitter() {
      this.ready = false;
      this.sharedDocument = await documentServer.getSharedDocument(
        this.sharedDocumentId
      );
      await this.setInitialValues();
      this.localSubmitId = this.sharedDocument.document?.id;
      this.form = await formServer.get(this.sharedDocument.form.id);
      let version = this.form.current;
      if (this.localSubmitId) {
        this.submit = await this.getFormSubmitById(this.localSubmitId);
        version = await formVersionServer.get(this.submit.versionId);
        this.documentStatus = this.submit.status;
      }
      const client = await catalogServer.getClient(
        this.sharedDocument.patientId
      );

      const eoc = await eocServer.get(this.sharedDocument.episodeOfCareId);
      const eocCares = await scheduleEventServer.query({
        episodeOfCareIds: [eoc.id],
        types: ["LOC"]
      });

      const primaryProvider = eoc.caseLoads.find(
        cL => cL.caseRole.type == "PrimaryProvider"
      )
        ? eoc.caseLoads.find(cL => cL.caseRole.type == "PrimaryProvider").user
        : null;
      const medicationScheduledList = await scheduledMedicationServer.query(
        eoc.id
      );

      const events = await this.getEocEvents(eoc.id, version);

      const clientFiltered = {
        id: client.id,
        mrn: client.mrn,
        firstName: client.firstName,
        middleName: client.middleName,
        lastName: client.lastName,
        dateOfBirth: client.dateOfBirth,
        email: client.email,
        phone: client.phone,
        ssn: client.ssn,
        ssnUnmasked: client.ssnUnmasked,
        gender: client.gender ? client.gender.name : "",
        race: client.race ? client.race.name : "",
        ethnicity: client.ethnicity ? client.ethnicity.name : "",
        language: client.language ? client.language.name : "",
        city: client.city,
        state: client.state ? client.state.name : "",
        maritalStatus: client.maritalStatus ? client.maritalStatus.name : "",
        occupation: client.occupation,
        zipcode: client.zipcode,
        addressLine1: client.addressLine1,
        addressLine2: client.addressLine2,
        episodeOfCare: {
          id: eoc.id,
          eocn: eoc.eocn,
          status: eoc.status,
          location: eoc.location
            ? {
                name: eoc.location.name,
                addressLine1: eoc.location.addressLine1,
                addressLine2: eoc.location.addressLine2,
                city: eoc.location.city,
                email: eoc.location.email,
                fax: eoc.location.fax,
                npiNo: eoc.location.npiNo,
                phoneNumber: eoc.location.phoneNumber,
                state: eoc.location.state,
                taxId: eoc.location.taxId,
                zipcode: eoc.location.zipcode
              }
            : {},
          startDate: eoc.startDate,
          endDate: eoc.endDate,
          additionalsReportResults: eoc.additionalsReportResults,
          referralSource: eoc.referralSource,
          primaryProvider: primaryProvider
            ? {
                id: primaryProvider.id,
                firstName: primaryProvider.firstName,
                lastName: primaryProvider.lastName,
                middleName: primaryProvider.middleName,
                credentials: primaryProvider.credentials
              }
            : null,
          eocCares: eocCares.map(eocC => ({
            day: formatDateOnlyMysql(eocC.startDateTime),
            levelOfCare:
              eocC.services.length > 0 ? eocC.services[0].service.name : ""
          })),
          locationObj: eoc.location,
          medicationScheduledList: medicationScheduledList
        },
        insurances: client.insurances,
        allergies: client.allergies,
        contacts: client.contacts,
        guarantors: client.guarantors,
        events
      };
      await this.initializeFormSubmitterForm({
        formId: this.form.id,
        formName: this.form.name,
        version: version,
        client: clientFiltered,
        eoc: eoc,
        document: this.submit ? this.submit : null,
        otherResults: this.otherResults,
        defaultValues: this.defaultValues,
        showType: "e-form",
        selectedLocation: this.locationId
      });
      if (version.content.properties.modalSize)
        this.lastModalSize = version.content.properties.modalSize;
      this.eoc = eoc;
      this.ready = true;
    },
    async getEocEvents(eocId, version) {
      const hasFetchEventsActivated = version.content.properties.fetchEvents;
      if (hasFetchEventsActivated) {
        const events = await scheduleEventServer.getEventsByEoc(eocId);
        return events;
      }
      return [];
    },
    async validateFormSubmit() {
      let withoutErrors = await this.validateFormSubmitterForm();
      return withoutErrors;
    },
    async submitForm() {
      var valid = true;
      if (this.preFormComponent) {
        valid = this.$refs[
          "preFormComponent"
        ].validateReleaseOfInformationSubmit();

        if (!valid) {
          this.$nextTick(() => {
            this.$refs["preFormComponent"].scrolltoErrors();
            this.validationErrorMessage = "Please fix errors and try again.";
          });
        }
      }
      if (valid) {
        this.submitting = true;
        this.validationErrorMessage = "";
        let result = true;
        if (this.config.patientPortalDefaultDocumentStatus != "Draft")
          result = await this.validateFormSubmit();
        if (result) {
          const bakedLayout = `<div>
              <div class="text-left">
                ${
                  this.preFormComponent
                    ? this.$refs.preFormComponent.getBakedLayout()
                    : ""
                }
              </div>
              ${this.$refs.formViewer.getBakedLayout()}
            </div>`;
          let score = null;
          if (this.form.current.content.properties.scoreFunctionBody) {
            score = this.superComputeFormSubmitterValue({
              elementId: this.form.current.content.id,
              property: "scoreFunctionValue",
              value:
                "{{" +
                this.form.current.content.properties.scoreFunctionBody +
                "}}",
              defaultValue: 0,
              statements: true
            });
          } else {
            const components = getFormBuilderComponents(
              this.form.current.content
            );
            const assessmentQuestions = components.filter(
              u => u.elType == "AssessmentQuestion"
            );
            if (assessmentQuestions.length > 0) {
              score = assessmentQuestions.reduce((acc, assessmentQuestion) => {
                const value = this.values[assessmentQuestion.id].value;
                if (typeof value === "number" && value >= 0) {
                  return acc + value;
                }
                return acc;
              }, 0);
            }
          }
          if (this.localSubmitId) {
            await this.editFormSubmitterForm({
              submitId: this.sharedDocumentId,
              mode: "Results",
              status:
                this.config.patientPortalDefaultDocumentStatus ?? "Pending",
              parentDocumentId: this.parentDocumentId,
              bakedLayout,
              careAppointmentId: this.appointmentId,
              score
            }).then(() => {
              this.submitting = false;
            });
          } else {
            await this.addFormSubmitterForm({
              sharedDocumentId: this.sharedDocumentId,
              mode: "Results",
              status:
                this.config.patientPortalDefaultDocumentStatus ?? "Pending",
              parentDocumentId: this.parentDocumentId,
              bakedLayout,
              score,
              careAppointmentId: this.appointmentId
            }).then(result => {
              this.localSubmitId = result.submitId;
              this.submitting = false;
            });
          }
          if (this.preFormComponent) {
            await this.$refs["preFormComponent"].submitComponentData(
              this.localSubmitId
            );
          }
          this.refetchDocuments = true;
          this.$swal({
            icon: "success",
            title: "Document Completed",
            timer: 3000
          });
          this.loadDocuments();
          await this.initializeFormSubmitter();
          this.renderDocument();
        } else {
          this.submitting = false;
          let firstErrorElement = document.querySelector(".is-invalid");
          firstErrorElement.scrollIntoView({
            behavior: "smooth",
            block: "center"
          });
          this.validationErrorMessage = "Please fix errors and try again.";
        }
      }
    },
    goBack() {
      this.$router.back();
    }
  },
  computed: {
    ...mapState("formSubmitter", {
      json: state => state.form.json,
      values: state => state.form.values,
      firstFormError: state =>
        Object.keys(state.form.errors).length > 0
          ? Object.keys(state.form.errors)[0]
          : null,
      hasUnsavedChanges: state => state.form.hasUnsavedChanges
    }),
    ...mapGetters("formSubmitter", [
      "getFormSubmitById",
      "superComputeFormSubmitterValue"
    ]),
    ...mapState("session", {
      patientId: state => state.user.patient.id,
      locationId: state => state.location.id
    }),
    ...mapState({
      config: state => state.session.systemSettings
    }),
    formComputed() {
      return {
        ...this.form,
        name:
          (this.form.showInOmt ? "Assessment" : this.form.name) || "Untitled",
        current: {
          createdAt: this.form.current?.createdAt || "--",
          createdBy: {
            firstName: this.form.current?.createdBy?.firstName || "N/A",
            lastName: this.form.current?.createdBy?.lastName || "N/A",
            fullName: this.form.current?.createdBy?.fullName || "N/A"
          }
        }
      };
    },
    isActionRequired() {
      if (!this.sharedDocument.readOnly) return true;
      if (!this.sharedDocument.document) return true;
      return false;
    },
    preFormComponent() {
      if (this.form && this.form.category == "releaseOfInformation") {
        return DocumentReleaseOfInformation;
      }
      return null;
    }
  }
};
</script>

<style lang="scss" scoped></style>

<style scoped>
.document-render-container >>> .general-container {
  background: transparent !important;
  height: auto !important;
}
.document-render-container::v-deep > .vh-100 {
  height: auto !important;
}
.document-render-container >>> .general-container .document-view {
  padding: 0;
  margin: 0 15px;
  max-width: 100% !important;
}
.fa-print {
  color: #0275d8;
}
</style>
